import React, {useEffect, useState, useRef, TouchEvent} from 'react';
import { Card, Button, Flex, Modal, notification, Space } from 'antd';
import {apiService} from "../../remote/apiService";
import {MemberPackage} from '../../remote/types/responses';
import styles from './MemberComponent.module.css';
import log from 'loglevel';
import { useDispatch } from 'react-redux';
import { setUser } from '../../store/slices/userSlice';
import { useMediaQuery } from 'react-responsive';

interface Props {
    isVisible: boolean;
    onClose: () => void;
}

const MemberComponent: React.FC<Props> = ({ isVisible, onClose }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [selectedMemberPackage, setSelectedMemberPackage] = useState<MemberPackage | null>(null);
    const [memberPackages, setMemberPackages] = useState<MemberPackage[]>([]);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [currentOrderId, setCurrentOrderId] = useState<string>('');
    
    // 添加响应式检测
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const isSmallMobile = useMediaQuery({ maxWidth: 480 });

    useEffect(() => {
        const fetchMemberPackages = async () => {
            try {
                const response = await apiService.getMemberPackages();
                if (response.code === 200) {
                    setMemberPackages(response.data);
                    // 默认选中第一个会员套餐
                    if (response.data.length > 0) {
                        setSelectedMemberPackage(response.data[0]);
                    }
                }
            } catch (error) {
                log.debug('获取会员计划失败:', error);
            }
        };

        if (isVisible) {
            fetchMemberPackages();
        }
    }, [isVisible]);

    async function onPayment() {
        if (!selectedMemberPackage) {
            notification.warning({
                message: '操作有误',
                description: '请先选择一个会员套餐',
            });
            return;
        }

        try {
            setLoading(true);
            const response = await apiService.createOrder(selectedMemberPackage.id);

            if (response.code === 200) {
                const payUrl = response.data.payUrl;
                setCurrentOrderId(response.data.orderId);
                
                // 创建一个临时div来放置表单
                const div = document.createElement('div');
                div.innerHTML = payUrl;
                document.body.appendChild(div);
                
                // 获取并提交表单
                const form = div.querySelector('form');
                if (form) {
                    form.setAttribute('target', '_blank');
                    form.submit();
                }
                
                // 清理临时div
                setTimeout(() => {
                    document.body.removeChild(div);
                }, 100);

                // 显示支付状态确认Modal
                setPaymentModalVisible(true);
            } else {
                notification.error({
                    message: '创建支付订单失败',
                    description: response.message,
                });
            }
        } catch (error) {
            notification.error({
                message: '支付失败',
                description: '创建支付订单时发生错误',
            });
        } finally {
            setLoading(false);
        }
    }

    const handlePaymentSuccess = async () => {
        try {
            setLoading(true);
            const response = await apiService.checkPaymentStatus(currentOrderId);
            const paymentStatusResponse = response.data
            setPaymentModalVisible(false);
            if (response.code === 200) {
                if (paymentStatusResponse.paymentStatus === '已支付') {
                    notification.success({
                        message: '支付成功',
                        description: '您的会员已经开通',
                    });
                    onClose();

                    // 更新会员状态
                    dispatch(setUser({
                        isMember: paymentStatusResponse.isMember,
                        memberExpire: paymentStatusResponse.memberExpire
                    }));

                    // 刷新页面
                    window.location.reload();
                    
                } else {
                    notification.warning({
                        message: '未完成支付'
                    });
                }
            } else {
                notification.error({
                    message: '支付失败'
                });
            }
        } catch (error) {
            notification.error({
                message: '支付失败'
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentCancel = () => {
        setPaymentModalVisible(false);
        notification.info({
            message: '支付已取消',
            description: '您可以稍后再试'
        });
    };

    // 添加触摸反馈处理
    const handleTouchStart = (e: TouchEvent<HTMLDivElement>, packageId: string) => {
        if (isMobile) {
            const element = e.currentTarget;
            element.style.opacity = '0.8';
        }
    };
    
    const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
        if (isMobile) {
            const element = e.currentTarget;
            element.style.opacity = '1';
        }
    };

    const renderCard = (memberPackage: MemberPackage) => {
        const cardClass = `${styles.pricingCard} ${selectedMemberPackage?.id === memberPackage.id ? styles.selectedCard : ''}`;
        
        return (
            <div 
                key={memberPackage.id}
                className={cardClass}
                onClick={() => setSelectedMemberPackage(memberPackage)}
                onTouchStart={(e) => handleTouchStart(e, memberPackage.id.toString())}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
            >
                {isMobile ? (
                    // 手机端布局 - 名称和价格并排
                    <div className={styles.pricingHeaderMobile}>
                        <h3>{memberPackage.name}</h3>
                        <div className={styles.pricingPrice}>
                            ¥{memberPackage.price}
                        </div>
                    </div>
                ) : (
                    // PC端布局 - 名称和价格上下排列
                    <div className={styles.pricingHeader}>
                        <h3>{memberPackage.name}</h3>
                        <div className={styles.pricingPrice}>
                            ¥{memberPackage.price}
                        </div>
                    </div>
                )}
                
                <div className={styles.pricingFeatures}>
                    {memberPackage.desc && memberPackage.desc.map((desc, index) => (
                        <div key={index} className={styles.pricingFeature}>
                            <span className={styles.featureCheck}>✓</span>
                            <p className={styles.featureText}>
                                {desc}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <>
            <Modal
                title={
                    <div className={styles.modalTitle}>
                        <h2 className={styles.modalTitleText}>开通会员</h2>
                    </div>
                }
                open={isVisible}
                onCancel={onClose}
                width={isMobile ? (isSmallMobile ? '95%' : '90%') : 700}
                centered
                footer={null}
                closable={isMobile}
                closeIcon={isMobile ? <span style={{fontSize: '20px'}}>&times;</span> : null}
                maskClosable={isMobile}
                bodyStyle={{
                    padding: isMobile ? '0' : '24px',
                    maxHeight: isMobile ? '60vh' : 'auto',
                    overflowY: isMobile ? 'auto' : 'visible'
                }}
            >
                <Flex vertical gap={isMobile ? "middle" : "large"} className={styles.modalContent}>
                    <div className={styles.pricingCards}>
                        {memberPackages.map((memberPackage) => renderCard(memberPackage))}
                    </div>
                    
                    <Flex justify="center" gap={isMobile ? "small" : "middle"} wrap={isMobile ? "wrap" : "nowrap"}>
                        <Button 
                            type="primary"
                            onClick={onPayment}
                            loading={loading}
                            disabled={!selectedMemberPackage}
                            className={styles.primaryButton}
                        >
                            确认协议并支付
                        </Button>
                        <Button 
                            onClick={onClose}
                            className={styles.button}
                        >
                            取消
                        </Button>
                    </Flex>
                    <div style={{ 
                        textAlign: 'center', 
                        fontSize: isMobile ? '12px' : '14px', 
                        marginTop: isMobile ? '5px' : '10px' 
                    }}>
                        开通前请阅读{' '}
                        <a 
                            href="/member-terms.html" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                        >
                            《极客ABC会员协议》
                        </a>
                    </div>
                </Flex>
            </Modal>

            <Modal
                title="支付状态确认"
                open={paymentModalVisible}
                footer={null}
                closable={true}
                onCancel={handlePaymentCancel}
                maskClosable={true}
                centered
                width={isMobile ? (isSmallMobile ? '90%' : '80%') : '500px'}
            >
                <div style={{ textAlign: 'center', padding: isMobile ? '15px 0' : '20px 0' }}>
                    <p style={{ marginBottom: isMobile ? '15px' : '20px' }}>请在新窗口中完成支付</p>
                    <p style={{ marginBottom: isMobile ? '15px' : '20px' }}>支付完成后，请确认您的支付状态</p>
                    <Space direction={isMobile ? "vertical" : "horizontal"} size={isMobile ? "small" : "middle"}>
                        <Button type="primary" onClick={handlePaymentSuccess} loading={loading}>
                            我已完成支付
                        </Button>
                        <Button onClick={handlePaymentCancel}>
                            取消支付
                        </Button>
                    </Space>
                </div>
            </Modal>
        </>
    );
};

export default MemberComponent;